import { ArrowUpTrayIcon } from "@heroicons/react/24/outline";
import { getTripPhotos, uploadTripPhotos } from "api/booking/booking";
import { useData } from "data/data-provider";
import { useCallback, useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import toast from "react-hot-toast";
import ButtonPrimary from "shared/Button/ButtonPrimary";

interface FileWithPreview extends File {
  preview: string;
}

export default function UploadZone({
  bookingId,
  preTrip,
}: {
  bookingId: any;
  preTrip: boolean;
}) {
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [uploadedImages, setUploadedImages] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(false);
  const {ourCompany} = useData()
  const [showAllImages, setShowAllImages] = useState(false); 
  const IMAGES_TO_SHOW = 5; 

  const fetchPhotos = async () => {
    try {
      const photos = await getTripPhotos(bookingId);

      if (Array.isArray(photos)) {
        setUploadedImages(photos); 
      } else {
        throw new Error(photos?.error || "Unexpected response format"); 
      }
    } catch (error) {
      console.error("Failed to fetch trip photos:", error);
      setUploadedImages([]); 
    }
  };

  useEffect(() => {
    fetchPhotos();
  }, [bookingId]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFiles((prevFiles) => [
      ...prevFiles,
      ...acceptedFiles.map((file) =>
        Object.assign(file, { preview: URL.createObjectURL(file) })
      ),
    ]);
  }, []);

  // Revoke object URLs when component unmounts or files change to avoid memory leaks
  useEffect(() => {
    return () => {
      files.forEach((file) => {
        try {
          URL.revokeObjectURL(file.preview);
        } catch (e) {
          // ignore
        }
      });
    };
  }, [files]);

  const startUpload = async () => {
    setIsLoading(true);
    try {
      const response = await uploadTripPhotos(files, bookingId, preTrip);

      if (response.error) {
        throw new Error(response.error);
      }

      const newPhotos = response.photos || []; // Assuming photos is the array of new photos
      if (!Array.isArray(newPhotos)) {
        throw new Error("Invalid response format: expected an array of photos");
      }

      // Append new photos to the state
      setUploadedImages((prevImages: any) => [...prevImages, ...newPhotos]);
      setFiles([]); // Clear files after successful upload
      toast.success("Files uploaded successfully");
      fetchPhotos();
    } catch (error) {
      toast.error("Upload failed");
    } finally {
      setIsLoading(false);
    }
  };

  const clearFiles = () => {
    setFiles([]);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/webp": [".webp"],
      "video/mp4": [".mp4"],
      "video/quicktime": [".mov"],
      "video/webm": [".webm"],
    },
  });

  const displayedImages = showAllImages
    ? uploadedImages
    : uploadedImages.slice(0, IMAGES_TO_SHOW);

  function formatDate(dateString: string) {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: ourCompany?.default_time_format !== '24_hour',
    };

    const formattedDate = date.toLocaleString("en-GB", options);

    return formattedDate;
  }

  return (
    <div className="  rounded-md">
      <div className="rounded-md">
        <div
          {...getRootProps()}
          className="cursor-pointer p-4 border-2 border-dashed border-secondary-100 rounded-md"
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center min-h-20">
            <div className="text-text-color mt-2">
              <ArrowUpTrayIcon className="w-5 h-5 lg:w-10 lg:h-10" />
            </div>
            <p className="text-text-color">Drop or select files</p>
          </div>
        </div>
        {!isLoading && files?.length > 0 && (
          <div>
            {/* Display images in a grid layout */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {files?.map((file, index) => (
                <div key={index} className="relative mt-4">
                  {file.type.startsWith("image") && (
                    <div className="group relative h-[115px] w-full rounded-md bg-gray-50">
                      <img
                        src={file.preview}
                        alt={file.name}
                        className="rounded-md object-cover h-full w-full"
                      />
                    </div>
                  )}

                  {file.type.startsWith("video") && (
                    <div className="group relative h-[115px] w-full rounded-md bg-black">
                      <video
                        src={file.preview}
                        className="rounded-md object-cover h-full w-full"
                        controls
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Clear and Upload buttons */}
            <div className="flex gap-4 mt-4">
              <button
                onClick={clearFiles}
                className="bg-secondary-100 text-text-color py-2 px-4 rounded-md flex items-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-5 h-5 mr-2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19.5 9.75L19.5 18.75C19.5 19.9926 18.4926 21 17.25 21L6.75 21C5.50736 21 4.5 19.9926 4.5 18.75L4.5 9.75M9.75 9.75V16.5M14.25 9.75V16.5M3 6.75H21M9.75 6.75V4.5C9.75 3.94772 10.1977 3.5 10.75 3.5H13.25C13.8023 3.5 14.25 3.94772 14.25 4.5V6.75"
                  />
                </svg>
                Clear {files.length} files
              </button>

              <button
                onClick={startUpload}
                className="bg-primary-100 text-text-color py-2 px-4 rounded-md flex items-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-5 h-5 mr-2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 16.5v3a1.5 1.5 0 001.5 1.5h15a1.5 1.5 0 001.5-1.5v-3M7.5 12l4.5-4.5m0 0L16.5 12m-4.5-4.5V21"
                  />
                </svg>
                Upload {files.length} files
              </button>
            </div>
          </div>
        )}

        {!files?.length && !uploadedImages?.length && (
          <p className="text-text-color mt-2">No files selected or uploaded</p>
        )}
      </div>

      {uploadedImages &&
        uploadedImages?.filter((image: any) =>
          preTrip
            ? image.image_type === "pre_trip"
            : image.image_type === "post_trip"
        ).length > 0 && (
          <>
            <h2 className="font-bold text-lg my-4 text-text-color">Uploaded Files</h2>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {displayedImages
                .filter((image: any) =>
                  preTrip ? image.image_type === "pre_trip" : image.image_type === "post_trip"
                )
                .map((image: any) => {
                  const src = image?.image;
                  const ext = src?.split("?")[0].split(".").pop()?.toLowerCase();
                  const isVideo = ["mp4", "mov", "webm"].includes(ext || "");
                  const mimeMap: Record<string, string> = {
                    mp4: "video/mp4",
                    mov: "video/quicktime",
                    webm: "video/webm",
                  };
                  return (
                    <div key={image.id} className="relative">
                      {isVideo ? (
                        <video
                          controls
                          className="rounded-md object-cover h-[150px] w-full bg-black"
                        >
                          <source src={src} type={mimeMap[ext || "mp4"]} />
                          Your browser does not support the video tag.
                        </video>
                      ) : (
                        <img
                          src={src}
                          alt={`Uploaded ${image.id}`}
                          className="rounded-md object-cover h-[150px] w-full"
                        />
                      )}

                      <p className="mt-2 font-bold text-text-color">
                        {image?.added_by?.first_name} {image?.added_by?.last_name}
                      </p>
                      <p className="text-text-color font-semibold">
                        {formatDate(image?.created_at)}
                      </p>
                    </div>
                  );
                })}
            </div>
            {uploadedImages?.filter((image: any) =>
              preTrip
                ? image.image_type === "pre_trip"
                : image.image_type === "post_trip"
            ).length > IMAGES_TO_SHOW && (
              <div className="flex justify-center mt-4">
                <ButtonPrimary
                  onClick={() => setShowAllImages(!showAllImages)}
                  className="mt-4 text-white "
                >
                  {showAllImages ? "View Less" : "View More"}
                </ButtonPrimary>
              </div>
            )}
          </>
        )}

      {/* File Preview */}
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4"></div>

      {/* Upload and Clear Buttons */}

      {/* Loading Spinner */}
      {isLoading && (
        <div className="flex justify-center mt-4">
          <LoadingSpinner />
        </div>
      )}
    </div>
  );
}

// Loading spinner component
function LoadingSpinner() {
  return (
    <svg
      className="animate-spin h-5 w-5 text-blue-500"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8"
      ></path>
    </svg>
  );
}
